import { useSyncExternalStore } from "react";
import { createAuthClient } from "better-auth/react";
import { emailOTPClient } from "better-auth/client/plugins";
import { authStore, type AuthSnapshot, type AuthUser } from "../stores/auth-store";
import { getApiUrl } from "./api";

// One client per API root per page. Pure bearer transport: the token comes
// from authStore (localStorage), never cookies — /widget/* answers CORS "*",
// and /customer-auth echoes the partner origin only when it is allowlisted.
const clients = new Map<string, ReturnType<typeof build>>();

function apiRoot(apiUrl?: string) {
  return (apiUrl || getApiUrl()).replace(/\/+$/, "");
}

function build(root: string, businessSlug?: string) {
  return createAuthClient({
    baseURL: `${root}/customer-auth`,
    fetchOptions: {
      auth: { type: "Bearer", token: () => authStore.getToken() ?? "" },
      // Lets the API brand the OTP email when several schools share this origin.
      headers: businessSlug ? { "X-Baluu-Business": businessSlug } : undefined,
      onSuccess: (ctx) => {
        const token = ctx.response.headers.get("set-auth-token");
        if (token) authStore.setToken(token);
      },
    },
    plugins: [emailOTPClient()],
  });
}

export function getAuthClient(apiUrl?: string, businessSlug?: string) {
  const root = apiRoot(apiUrl);
  const key = `${root}|${businessSlug ?? ""}`;
  let client = clients.get(key);
  if (!client) { client = build(root, businessSlug); clients.set(key, client); }
  return client;
}

function toUser(u: { id: string; email: string; name?: string | null }): AuthUser {
  return { id: u.id, email: u.email, name: u.name ?? "" };
}

/**
 * Whether the store still holds the token this in-flight `getSession` was
 * asking about — i.e. whether our answer is still the answer to the current
 * question.
 *
 * Both session resolvers below await a network round trip and then write the
 * store. Without this check the write is applied blind, and a slow validation
 * of a DEAD token can land after a fresh sign-in and undo it: `data.user` is
 * falsy (the old token really was invalid), so `setAnonymous()` runs and wipes
 * the brand-new token out of localStorage, signing the customer out moments
 * after they signed in, with no error anywhere.
 *
 * Comparing the token VALUE is a sound "still mine" test here, because the
 * only writer that can change it mid-flight without meaning to invalidate us
 * is our own response: the client's `onSuccess` hook re-stores `set-auth-token`
 * when better-auth re-issues the session cookie. That header carries the SAME
 * string — better-auth's session refresh calls `updateSession(session.token,
 * { expiresAt })`, keeping the token and re-signing it with a deterministic
 * HMAC, so `getSession` never rotates the value (verified against better-auth
 * 1.7.3, `dist/api/routes/session.mjs` + `dist/plugins/bearer/index.mjs`).
 * Every OTHER writer — a fresh sign-in, a sign-out, another tab's `storage`
 * event — necessarily changes it, which is exactly the case we must not
 * overwrite.
 */
function stillCurrent(token: string | null): boolean {
  return authStore.getToken() === token;
}

/** Validate a stored token once per page; invalid → anonymous (fail open). */
let hydration: Promise<void> | null = null;
export function hydrateSession(apiUrl?: string, businessSlug?: string): Promise<void> {
  const snap = authStore.getSnapshot();
  if (snap.status !== "unknown" || !snap.token) return Promise.resolve();
  if (hydration) return hydration;
  // Captured BEFORE the await: this is the token the round trip below is
  // validating, and the only one its verdict may be applied to.
  const validating = snap.token;
  hydration = (async () => {
    try {
      const { data } = await getAuthClient(apiUrl, businessSlug).getSession({ query: { disableCookieCache: true } });
      // Something newer won the store while we were in flight (a completed
      // sign-in, a sign-out, another tab). Our verdict is about a token nobody
      // holds any more — drop it rather than stamp it over theirs.
      if (!stillCurrent(validating)) return;
      if (data?.user) authStore.setAuthenticated(validating, toUser(data.user));
      else authStore.setAnonymous();
    } catch {
      // Same guard on the failure path: a network blip must not sign out a
      // customer who signed in while this request was hanging.
      if (stillCurrent(validating)) authStore.setAnonymous();
    } finally {
      hydration = null;
    }
  })();
  return hydration;
}

/** Called by the OTP dialog after signIn.emailOtp succeeds. */
export async function completeSignIn(apiUrl?: string, businessSlug?: string): Promise<AuthUser | null> {
  // `signIn.emailOtp`'s own onSuccess has already stored the new token, so
  // this is the token being confirmed. Captured before the await for the same
  // reason as above — and so the user we resolve is paired with the token it
  // was actually resolved from, never with whatever happens to be in the store
  // when the response lands.
  const confirming = authStore.getToken();
  const { data } = await getAuthClient(apiUrl, businessSlug).getSession({ query: { disableCookieCache: true } });
  if (!stillCurrent(confirming)) return null;
  if (data?.user && confirming) { const user = toUser(data.user); authStore.setAuthenticated(confirming, user); return user; }
  authStore.setAnonymous();
  return null;
}

export async function signOutCustomer(apiUrl?: string, businessSlug?: string): Promise<void> {
  try { await getAuthClient(apiUrl, businessSlug).signOut(); } catch { /* token may already be dead */ }
  authStore.setAnonymous();
}

export function useAuthSnapshot(): AuthSnapshot {
  return useSyncExternalStore(authStore.subscribe, authStore.getSnapshot, authStore.getSnapshot);
}
